import React from 'react';
import Dialog from '../Dialog/Dialog';

/**
 * DialogShowExternalContent — v2 molecule.
 *
 * Modal para embeber contenido externo (HTML, mapas, recursos web) en un
 * `<iframe>` dentro de un `Dialog` v2 `size="xl"`. El caller es responsable
 * de construir la URL final (`src`).
 *
 * Accesibilidad (WCAG 2.1 AA):
 *  - `title` es obligatorio y se propaga al `<iframe title>` (WCAG 4.1.2):
 *    los lectores de pantalla anuncian el contenido embebido por su
 *    título, no por la URL.
 *  - El dialog hereda focus trap, cierre por `Escape`, `aria-modal="true"`
 *    y devolución de foco al trigger desde `Dialog` v2.
 *  - `sandbox` es opt-in (no tiene default) para preservar el comportamiento
 *    de v1. Pásalo cuando quieras restringir capacidades del iframe (ej.
 *    `"allow-scripts allow-same-origin"`) por motivos de seguridad.
 *  - `referrerPolicy="strict-origin-when-cross-origin"` evita filtrar la URL
 *    completa (con guids de curso/tenant) al origen embebido.
 *  - `loading="lazy"` difiere la carga del iframe hasta que esté en viewport.
 */

type DialogShowExternalContentProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  sandbox?: string;
  src: string;
  title: string;
}>;

function DialogShowExternalContent({
  isOpen,
  onClose,
  sandbox,
  src,
  title,
}: DialogShowExternalContentProps): React.JSX.Element {
  return (
    <Dialog className="dialog-show-external-content" isOpen={isOpen} onClose={onClose} size="xl" title={title}>
      <div className="dialog-show-external-content__wrapper">
        <iframe
          className="dialog-show-external-content__iframe"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox={sandbox}
          src={src}
          title={title}
        />
      </div>
    </Dialog>
  );
}

export default DialogShowExternalContent;
